import React from "react";
import { Link } from "react-router-dom";
import { Reveal, SectionOverline } from "./Shared";
import {
  Compass,
  Flame,
  Wind,
  Repeat,
  Shield,
  Trophy,
  ArrowRight,
} from "lucide-react";

const AUDIENCES = [
  {
    icon: Compass,
    title: "In recovery",
    body:
      "For those retraining a nervous system that has adapted to substance use and coping cycles.",
    testId: "audience-recovery",
    to: "/applications/recovery",
  },
  {
    icon: Flame,
    title: "In chronic stress or burnout",
    body:
      "For a physiology stuck in over-activation — where rest no longer produces recovery.",
    testId: "audience-burnout",
    to: "/applications/stress-burnout",
  },
  {
    icon: Wind,
    title: "Living with anxiety-driven behavior",
    body:
      "For patterns that fire before thought — retrain the state and the behavior follows.",
    testId: "audience-anxiety",
    to: "/applications/anxiety-regulation",
  },
  {
    icon: Repeat,
    title: "Working on habit & behavior change",
    body:
      "For behaviors that resist willpower because the state driving them hasn’t changed.",
    testId: "audience-long-term",
    to: "/applications/habit-change",
  },
  {
    icon: Shield,
    title: "Carrying trauma-related adaptations",
    body:
      "For nervous systems still running protective patterns past their original context.",
    testId: "audience-recurring",
    to: "/applications/trauma-adaptation",
  },
  {
    icon: Trophy,
    title: "High performers & athletes",
    body:
      "For resilience, recovery, and nervous system regulation at the edge of capacity.",
    testId: "audience-performers",
    to: "/applications/performance",
  },
];

const WhoThisHelps = () => {
  return (
    <section
      id="who-this-helps"
      data-testid="who-this-helps-section"
      className="relative py-32 sm:py-40 border-t forr-canvas-alt"
      style={{ borderColor: "var(--forr-line-soft)" }}
    >
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
        <div className="grid grid-cols-12 gap-8">
          <div className="col-span-12 lg:col-span-5">
            <Reveal>
              <SectionOverline
                number="07"
                label="Who This Helps"
                testId="who-this-helps-overline"
              />
            </Reveal>
            <Reveal delay={0.05}>
              <h2
                data-testid="who-this-helps-heading"
                className="mt-8 font-display forr-text-ink text-4xl sm:text-5xl md:text-6xl font-medium tracking-[-0.02em] leading-[1.05]"
              >
                Not patients.{" "}
                <span className="forr-text-mute">
                  People retraining their physiology.
                </span>
              </h2>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-8 max-w-md text-lg font-light forr-text-soft leading-relaxed">
                Adaptive Physiology is designed for anyone whose nervous system
                has learned patterns they’re ready to unlearn.
              </p>
            </Reveal>
          </div>

          <div className="col-span-12 lg:col-span-7 lg:pl-8">
            <div className="grid sm:grid-cols-2 gap-6">
              {AUDIENCES.map((a, i) => (
                <Reveal delay={i * 0.06} key={a.title}>
                  <Link
                    to={a.to}
                    data-testid={`card-${a.testId}`}
                    className="forr-card rounded-[24px] p-8 h-full block group"
                  >
                    <div className="flex items-center justify-between mb-8">
                      <div
                        className="w-11 h-11 rounded-2xl flex items-center justify-center"
                        style={{
                          backgroundColor: "var(--forr-teal-soft)",
                          color: "var(--forr-teal-deep)",
                        }}
                      >
                        <a.icon size={20} strokeWidth={1.5} />
                      </div>
                      <span className="text-[11px] uppercase tracking-[0.24em] forr-text-faint">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                    </div>
                    <h3 className="font-display forr-text-ink text-lg font-medium tracking-tight mb-3">
                      {a.title}
                    </h3>
                    <p className="text-sm font-light forr-text-soft leading-relaxed">
                      {a.body}
                    </p>
                    <div className="mt-6 inline-flex items-center gap-2 forr-text-teal-deep text-sm font-medium">
                      Read the application
                      <ArrowRight
                        size={14}
                        strokeWidth={1.75}
                        className="transition-transform duration-300 group-hover:translate-x-1"
                      />
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhoThisHelps;
